import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiGet, apiDelete } from "../../services/api/apiClient";

type LoadingStatus = "idle" | "loading" | "succeeded" | "failed";

interface VendorMergesState {
  items: unknown[];
  status: LoadingStatus;
  error: string | null;
  page: number;
  limit: number;
  total: number | null;
  totalPages: number | null;
  filters: { year: string; from: string; to: string; createdBy: string };
  generateStatus: LoadingStatus;
  generateError: string | null;
  generateSuccess: string | null;
  mergeStatus: LoadingStatus;
  mergeError: string | null;
  mergeSuccess: string | null;
  mergeInProgress: string | null;
  missingReportStatus: LoadingStatus;
  missingReportError: string | null;
  missingReportSuccess: string | null;
  deleteStatus: LoadingStatus;
  deleteError: string | null;
  deleteSuccess: string | null;
}

const initialState: VendorMergesState = {
  items: [],
  status: "idle",
  error: null,

  page: 1,
  limit: 10,
  total: null,
  totalPages: null,

  filters: {
    year: "",
    from: "",
    to: "",
    createdBy: "",
  },

  generateStatus: "idle",
  generateError: null,
  generateSuccess: null,
  mergeStatus: "idle",
  mergeError: null,
  mergeSuccess: null,
  mergeInProgress: null,
  missingReportStatus: "idle",
  missingReportError: null,
  missingReportSuccess: null,
  deleteStatus: "idle",
  deleteError: null,
  deleteSuccess: null,
};

export const fetchVendorInsuranceMerges = createAsyncThunk(
  "vendorMerges/fetch",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as any;
      const { page, limit, filters } = state.vendorMerges;
      const params: any = {};
      if (filters.year) params.year = filters.year;
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
      if (filters.createdBy) params.createdBy = filters.createdBy;
      if (page) params.page = page;
      if (limit) params.limit = limit;

      const data = await apiGet({
        endpoint: "/vendor-insurance-merges",
        params,
        withAuth: true,
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch merges");
    }
  }
);

export const generateXlsxReport = createAsyncThunk(
  "vendorMerges/generateXlsx",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as any;
      const year = state.vendorMerges?.filters?.year || undefined;
      const BASE_URL = import.meta.env.VITE_BASE_URL || "";
      const token = localStorage.getItem("user")
        ? JSON.parse(localStorage.getItem("user") || "null").token
        : null;
      const params = year ? `?year=${encodeURIComponent(year)}` : "";
      const url = `${BASE_URL}/vendor-insurance-report${params}`;
      const fetchHeaders: HeadersInit = {
        Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      };
      if (token) (fetchHeaders as Record<string, string>)["Authorization"] = `Bearer ${token}`;
      const res = await fetch(url, {
        method: "GET",
        headers: fetchHeaders,
      });
      if (!res.ok)
        throw new Error("Failed to download vendor insurance report");
      const blob = await res.blob();
      const dlUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = `vendor_insurance_report${year ? `_${year}` : ""}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(dlUrl);
      document.body.removeChild(a);
      return { success: true };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to generate XLSX");
    }
  }
);

export const initiateFilesMerge = createAsyncThunk(
  "vendorMerges/initiateMerge",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as any;
      const { year } = state.vendorMerges?.filters || {};
      const BASE_URL = import.meta.env.VITE_BASE_URL || "";
      const token = localStorage.getItem("user")
        ? JSON.parse(localStorage.getItem("user") || "null").token
        : null;
      const params = year ? `?year=${encodeURIComponent(year)}` : "";
      const url = `${BASE_URL}/vendor-insurance-report-with-files${params}`;
      const res = await fetch(url, {
        method: "GET",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          Accept: "text/plain, application/json",
        },
      });

      // Handle 409 - Merge already in progress
      if (res.status === 409) {
        const text = await res.text();
        return rejectWithValue({ status: 409, message: text });
      }

      if (!res.ok) throw new Error("Failed to merge vendor insurance files");
      const contentType = res.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const data = await res.json();
        return { message: data.message || "Files merged successfully" };
      }
      const text = await res.text();
      return { message: text || "Files merged successfully" };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to merge files");
    }
  }
);

export const getMissingFilesReport = createAsyncThunk(
  "vendorMerges/missingReport",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as any;
      const year = state.vendorMerges?.filters?.year || undefined;
      const BASE_URL = import.meta.env.VITE_BASE_URL || "";
      const token = localStorage.getItem("user")
        ? JSON.parse(localStorage.getItem("user") || "null").token
        : null;
      const params = year ? `?year=${encodeURIComponent(year)}` : "";
      const url = `${BASE_URL}/missing-vendors-report${params}`;
      const res = await fetch(url, {
        method: "GET",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          Accept: "text/plain, application/json",
        },
      });
      if (!res.ok) throw new Error("Failed to download missing vendors report");
      const contentType = res.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const data = await res.json();
        return data;
      }
      // Treat as plain text
      const text = await res.text();
      const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
      const dlUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = `missing_vendors_report${year ? `_${year}` : ""}.txt`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(dlUrl);
      document.body.removeChild(a);
      return { success: true };
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to fetch missing-files report"
      );
    }
  }
);

export const deleteVendorInsuranceMerge = createAsyncThunk(
  "vendorMerges/delete",
  async (mergeId, { rejectWithValue }) => {
    try {
      await apiDelete({
        endpoint: `/vendor-insurance-merge/${mergeId}`,
        withAuth: true,
      });
      return { mergeId };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to delete merge file");
    }
  }
);

const vendorMergesSlice = createSlice({
  name: "vendorMerges",
  initialState,
  reducers: {
    setFilter(state, action) {
      const { name, value } = action.payload;
      (state.filters as any)[name] = value;
    },
    setPage(state, action) {
      state.page = action.payload;
    },
    setLimit(state, action) {
      state.limit = action.payload;
    },
    clearItems(state) {
      state.items = [];
    },
    clearGenerateSuccess(state) {
      state.generateSuccess = null;
    },
    clearMergeSuccess(state) {
      state.mergeSuccess = null;
    },
    clearMergeInProgress(state) {
      state.mergeInProgress = null;
    },
    clearMissingReportSuccess(state) {
      state.missingReportSuccess = null;
    },
    clearDeleteSuccess(state) {
      state.deleteSuccess = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVendorInsuranceMerges.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchVendorInsuranceMerges.fulfilled, (state, action) => {
        state.status = "succeeded";
        // API returns { data: [...], pagination: {...} }
        if (action.payload && Array.isArray(action.payload.data)) {
          state.items = action.payload.data;
          if (action.payload.pagination) {
            state.total = action.payload.pagination.total;
            state.totalPages = action.payload.pagination.totalPages;
            state.page = action.payload.pagination.page;
          }
        } else if (Array.isArray(action.payload)) {
          state.items = action.payload;
        } else if (action.payload && Array.isArray(action.payload.items)) {
          state.items = action.payload.items;
          if (typeof action.payload.total === "number") {
            state.total = action.payload.total;
            state.totalPages = Math.ceil(action.payload.total / state.limit);
          } else if (typeof action.payload.totalPages === "number") {
            state.totalPages = action.payload.totalPages;
          }
        } else {
          state.items = [];
        }
      })
      .addCase(fetchVendorInsuranceMerges.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })

      .addCase(generateXlsxReport.pending, (state) => {
        state.generateStatus = "loading";
        state.generateError = null;
        state.generateSuccess = null;
      })
      .addCase(generateXlsxReport.fulfilled, (state) => {
        state.generateStatus = "succeeded";
        state.generateSuccess = "XLSX report downloaded successfully";
      })
      .addCase(generateXlsxReport.rejected, (state, action) => {
        state.generateStatus = "failed";
        state.generateError = action.payload as string;
      })

      .addCase(initiateFilesMerge.pending, (state) => {
        state.mergeStatus = "loading";
        state.mergeError = null;
        state.mergeSuccess = null;
        state.mergeInProgress = null;
      })
      .addCase(initiateFilesMerge.fulfilled, (state) => {
        state.mergeStatus = "succeeded";
        state.mergeSuccess = "Merge initiated successfully";
      })
      .addCase(initiateFilesMerge.rejected, (state, action) => {
        state.mergeStatus = "failed";
        const payload = action.payload as { status?: number; message?: string } | undefined;
        // Check if it's a 409 status (merge in progress)
        if (payload?.status === 409) {
          state.mergeInProgress = payload.message ?? null;
          state.mergeError = null;
        } else {
          state.mergeError =
            payload?.message ||
            (typeof action.payload === "string" ? action.payload : null) ||
            "Failed to merge files";
          state.mergeInProgress = null;
        }
      })

      .addCase(getMissingFilesReport.pending, (state) => {
        state.missingReportStatus = "loading";
        state.missingReportError = null;
        state.missingReportSuccess = null;
      })
      .addCase(getMissingFilesReport.fulfilled, (state) => {
        state.missingReportStatus = "succeeded";
        state.missingReportSuccess =
          "Missing files report downloaded successfully";
      })
      .addCase(getMissingFilesReport.rejected, (state, action) => {
        state.missingReportStatus = "failed";
        state.missingReportError = action.payload as string;
      })

      .addCase(deleteVendorInsuranceMerge.pending, (state) => {
        state.deleteStatus = "loading";
        state.deleteError = null;
        state.deleteSuccess = null;
      })
      .addCase(deleteVendorInsuranceMerge.fulfilled, (state, action) => {
        state.deleteStatus = "succeeded";
        state.deleteSuccess = "Merge file deleted successfully";
        // Remove the deleted item from the list
        state.items = state.items.filter(
          (item: any) => item.id !== action.payload.mergeId
        );
        // Update total if available
        if (typeof state.total === "number" && state.total > 0) {
          state.total -= 1;
          state.totalPages = Math.ceil(state.total / state.limit);
        }
      })
      .addCase(deleteVendorInsuranceMerge.rejected, (state, action) => {
        state.deleteStatus = "failed";
        state.deleteError = action.payload as string;
      });
  },
});

export const {
  setFilter,
  setPage,
  setLimit,
  clearItems,
  clearGenerateSuccess,
  clearMergeSuccess,
  clearMergeInProgress,
  clearMissingReportSuccess,
  clearDeleteSuccess,
} = vendorMergesSlice.actions;

export default vendorMergesSlice.reducer;
